import { pickEnum, searchableSource, type OptionQuery } from '@eo/shared';

import type {
  CapacityReport,
  ClientListParams,
  EntryListParams,
  ListParams,
  PortfolioTotals,
  StaffReport,
} from './api/types';
import { CLIENT_GRID_PREFIX, DETAIL_PANES, LOG_GRID_PREFIX } from './constants';
import type {
  CompositionSegment,
  DetailPane,
  CapacityState,
  LogFilters,
  OptionPickers,
  SearchFilterSource,
  Selection,
  TeamCategoryRow,
  TeamLoadRow,
} from './types';

// One grid's page, sort and search, read from its prefixed URL params.
export function gridListParams(
  prefix: string,
  query: Record<string, string | undefined>,
): ListParams {
  const page = Number(query[`${prefix}page`]);
  return {
    page: Number.isInteger(page) && page > 0 ? page : undefined,
    sort: query[`${prefix}sort`] || undefined,
    search: query[`${prefix}search`] || undefined,
  };
}

export function clientListParams(query: Record<string, string | undefined>): ClientListParams {
  return {
    clientId: query[`${CLIENT_GRID_PREFIX}client`] || undefined,
    ...gridListParams(CLIENT_GRID_PREFIX, query),
  };
}

// The client or person whose detail is open; a client wins if a URL names both.
export function parseSelection(query: Record<string, string | undefined>): Selection | null {
  if (query.client) return { kind: 'client', id: query.client };
  if (query.staff) return { kind: 'member', id: query.staff };
  return null;
}

export function parseDetailPane(raw: string | undefined): DetailPane {
  return pickEnum(DETAIL_PANES, raw ?? null, 'summary');
}

// The log narrows to the open selection, then to whoever or whichever client
// was picked from its lists, plus its own grid params.
export function entryListParams(
  selection: Selection,
  query: Record<string, string | undefined>,
): EntryListParams {
  const billable = query[`${LOG_GRID_PREFIX}billable`];
  return {
    clientId:
      selection.kind === 'client' ? selection.id : query[`${LOG_GRID_PREFIX}client`] || undefined,
    userId:
      selection.kind === 'member' ? selection.id : query[`${LOG_GRID_PREFIX}member`] || undefined,
    billable: billable === 'billable' ? true : billable === 'nonbillable' ? false : undefined,
    ...gridListParams(LOG_GRID_PREFIX, query),
  };
}

// The portfolio a client filter searches within: its owner, or every client.
export function portfolioFilters(report: CapacityReport): OptionQuery['filters'] {
  return report.scope.ownerUserId ? { ownerUserId: report.scope.ownerUserId } : undefined;
}

export function clientFilterSource(
  state: CapacityState,
  report: CapacityReport,
  pickedId: string | null,
  onQueryOptions: OptionPickers['onQueryOptions'],
): SearchFilterSource {
  return {
    options: state.options.clients.items.map((c) => ({ value: c.id, label: c.name })),
    selectedLabel: report.clients.find((c) => c.clientId === pickedId)?.name,
    ...searchableSource(state.options.clients, (q) =>
      onQueryOptions({ source: 'clients', ...q, filters: portfolioFilters(report) }),
    ),
  };
}

export function personFilterSource(
  state: CapacityState,
  report: CapacityReport,
  pickedId: string | null,
  onQueryOptions: OptionPickers['onQueryOptions'],
): SearchFilterSource {
  return {
    options: state.options.people.items.map((p) => ({ value: p.userId, label: p.name })),
    selectedLabel: report.users.find((u) => u.userId === pickedId)?.name,
    ...searchableSource(state.options.people, (q) => onQueryOptions({ source: 'people', ...q })),
  };
}

// The side already open has no filter: a client's log narrows by person, a
// person's log by client.
export function logFilters(
  state: CapacityState,
  report: CapacityReport,
  selection: Selection,
  query: URLSearchParams,
  onQueryOptions: OptionPickers['onQueryOptions'],
): LogFilters {
  const picked = (key: string) => query.get(`${LOG_GRID_PREFIX}${key}`);
  return selection.kind === 'client'
    ? { people: personFilterSource(state, report, picked('member'), onQueryOptions), clients: null }
    : {
        people: null,
        clients: clientFilterSource(state, report, picked('client'), onQueryOptions),
      };
}

export function selectionName(report: CapacityReport, selection: Selection): string {
  return selection.kind === 'client'
    ? (report.clients.find((c) => c.clientId === selection.id)?.name ?? 'Client')
    : (report.users.find((u) => u.userId === selection.id)?.name ?? 'Person');
}

// The URL that opens one client's or person's detail, closing whatever was open
// and starting its log afresh; everything else on the page stays as it was.
export function selectionSearch(current: URLSearchParams, next: Selection | null): string {
  const params = new URLSearchParams(current);
  for (const key of [...params.keys()]) {
    if (
      ['client', 'staff', 'pane', 'tab', 'view'].includes(key) ||
      key.startsWith(LOG_GRID_PREFIX)
    ) {
      params.delete(key);
    }
  }
  if (next) params.set(next.kind === 'client' ? 'client' : 'staff', next.id);
  return `?${params.toString()}`;
}

export function portfolioName(report: CapacityReport): string | null {
  const owner = report.scope.ownerUserId;
  if (owner === null) return null;
  return (
    report.portfolios.find((p) => p.ownerUserId === owner)?.name ??
    report.users.find((u) => u.userId === owner)?.name ??
    null
  );
}

export function compositionSegments(
  totals: PortfolioTotals,
  bucket: 'fixed' | 'variable',
): CompositionSegment[] {
  const key = bucket === 'fixed' ? 'assignedFixed' : 'assignedVariable';
  return [
    { key: 'cfo', label: 'CFO', hours: totals.byRole.cfo[key] },
    { key: 'dataTeam', label: 'Data team', hours: totals.byRole.dataTeam[key] },
    { key: 'unassigned', label: 'unassigned', hours: totals.unassigned[bucket] },
  ];
}

// Everyone with hours on the portfolio, CFOs first, the most assigned on top.
export function teamLoadRows(users: StaffReport[]): TeamLoadRow[] {
  return users
    .filter((u) => u.onPortfolio.assigned > 0 || u.onPortfolio.actual > 0)
    .map((u) => ({
      userId: u.userId,
      name: u.name,
      role: u.role === 'data_team' ? ('dataTeam' as const) : ('cfo' as const),
      assigned: u.onPortfolio.assigned,
      actual: u.onPortfolio.actual,
    }))
    .sort(
      (a, b) =>
        Number(a.role === 'dataTeam') - Number(b.role === 'dataTeam') ||
        b.assigned - a.assigned ||
        a.name.localeCompare(b.name),
    );
}

export function barPercent(hours: number, scale: number): string {
  return `${scale > 0 ? (hours / scale) * 100 : 0}%`;
}

export function monthShare(progress: CapacityReport['progress']): number {
  return progress.workingDays > 0 ? progress.elapsedWorkingDays / progress.workingDays : 0;
}

export function formatHours(value: number): string {
  return `${value.toFixed(2)}h`;
}

export function utilizationPct(actual: number, budget: number): number {
  return budget > 0 ? Math.round((actual / budget) * 100) : 0;
}

export function teamCategoryRows(member: StaffReport): TeamCategoryRow[] {
  const share = (pct: number) => (member.capacity.netHours * pct) / 100;
  const split = member.split;
  return [
    {
      id: 'csd',
      label: 'Client Service Delivery',
      pct: split?.csd,
      actual: member.categoryActuals.csd,
    },
    { id: 'rnd', label: 'R&D', pct: split?.rnd, actual: member.categoryActuals.rnd },
    {
      id: 'opsAdmin',
      label: 'Ops & Admin',
      pct: split?.opsAdmin,
      actual: member.categoryActuals.opsAdmin,
    },
  ].map(({ id, label, pct, actual }) => ({
    id,
    label,
    splitPct: pct ?? null,
    capacityShare: pct === undefined ? null : share(pct),
    actual,
  }));
}

// The report is a cache now, so its age is part of the reading. Anything older
// than a day states the date rather than a widening minute count.
export function freshnessLabel(syncedAt: string | null, now: number = Date.now()): string {
  if (!syncedAt) return 'Never synced';
  const ageMs = now - Date.parse(syncedAt);
  if (!Number.isFinite(ageMs) || ageMs < 0) return 'Synced just now';

  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return 'Synced just now';
  if (minutes < 60) return `Synced ${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Synced ${hours} hour${hours === 1 ? '' : 's'} ago`;

  return `Synced ${new Date(syncedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`;
}
